import * as React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import AnonymousOnlyRoute from './AnonymousOnlyRoute';
import ForgotPassword from './pages/password/ForgotPassword';
import Helmet from '../commonComponents/Helmet';
import Loading from '../commonComponents/Loading';
import LoginContainer from './pages/login/LoginContainer';
import favicon from '../images/favicon.png';
import SetPassword from './pages/password/SetPassword';
import Invitation from './pages/Invitation';
import RefereeDeclined from './pages/refereeDeclined/RefereeDeclined';
import RefereeValidation from './pages/refereeValidation/RefereeValidation';
import RefereeQuestions from './pages/refereeQuestions/RefereeQuestions';
import RefereeReview from './pages/refereeReview/RefereeReview';
import RefereeThanks from './pages/refereeThanks/RefereeThanks';
import RefereeSaved from './pages/refereeSaved/RefereeSaved';
import ErrorBoundary from './ErrorBoundary';
import { isReadOnlyUser, logOut, setBrandNameQueryStringsInLocalStorage } from '../core/session';
import './App.less';
import { fourHours } from '../core/constants';
import Integration from './pages/login/Integration';
import ThirdPartyIntegrations from './pages/login/ThirdPartyIntegrations';
import BullhornAuthorization from './pages/login/BullhornAuthorization';
import JobAdderAuthorization from './pages/login/JobAdderAuthorization';
import SmartRecruitersConsent from './pages/login/SmartRecruitersConsent';
import ConnectIdResponse from './pages/login/ConnectIdResponse';
import RefereeLogin from './pages/login/RefereeLogin';
import ClientRegistrationForm from './pages/ClientRegistrationForm';
import ToastContainer from '../commonComponents/ToastContainer';

const App = () => {
	const readOnlyUser = isReadOnlyUser();
	setBrandNameQueryStringsInLocalStorage();
	const onIdle = () => {
		if (getRemainingTime() === 0) logOut();
	};
	const { getRemainingTime } = useIdleTimer({
		onIdle,
		timeout: fourHours,
		promptTimeout: 0,
		events: [
			'mousemove',
			'keydown',
			'wheel',
			'DOMMouseScroll',
			'mousewheel',
			'mousedown',
			'touchstart',
			'touchmove',
			'MSPointerDown',
			'MSPointerMove',
			'visibilitychange'
		],
		immediateEvents: [],
		debounce: 0,
		throttle: 0,
		eventsThrottle: 200,
		element: document,
		startOnMount: true,
		startManually: false,
		stopOnIdle: false,
		crossTab: false,
		name: 'idle-timer',
		syncTimers: 0,
		leaderElection: false
	});
	return (
		<ErrorBoundary>
			<Helmet defaultTitle="VETTING.com Web App" titleTemplate="VETTING.com Web App | %s">
				<link rel="shortcut icon" type="image/png" href={favicon} />
			</Helmet>

			<BrowserRouter>
				<Routes>
					<Route
						path="/"
						element={
							<AnonymousOnlyRoute>
								<LoginContainer />
							</AnonymousOnlyRoute>
						}
					/>
					<Route
						path="/register"
						element={
							<>
								<ToastContainer autoClose={2500} />
								<AnonymousOnlyRoute>
									<ClientRegistrationForm />
								</AnonymousOnlyRoute>
							</>
						}
					/>
					<Route
						path="/forgot-password"
						element={
							<AnonymousOnlyRoute>
								<ForgotPassword />
							</AnonymousOnlyRoute>
						}
					/>
					<Route path="/invite" element={<Invitation />} />
					<Route path="/set-password" element={<SetPassword />} />
					<Route path="/integration/smartrecruiters/login" element={<LoginContainer />} />
					<Route path="/consent/:id" element={<SmartRecruitersConsent />} />
					<Route path="/referee" element={<RefereeLogin />} />
					{!readOnlyUser && (
						<>
							<Route path="/referee/validation" element={<RefereeValidation />} />
							<Route path="/referee/declined" element={<RefereeDeclined />} />
							<Route path="/referee/questions" element={<RefereeQuestions />} />
							<Route path="/referee/review" element={<RefereeReview />} />
							<Route path="/referee/thanks" element={<RefereeThanks />} />
							<Route path="/referee/saved" element={<RefereeSaved />} />
							<Route path="/integration" element={<Integration />} />
							<Route path="/integration/bullhorn/auth" element={<BullhornAuthorization />} />
							<Route path="/integration/jobadder/auth" element={<JobAdderAuthorization />} />
							<Route path="/integration/*" element={<ThirdPartyIntegrations />} />
							<Route path="/connectId/*" element={<ConnectIdResponse />} />
						</>
					)}
					<Route
						path="*"
						element={
							<React.Suspense fallback={<Loading />}>
								<LazyRoutes />
							</React.Suspense>
						}
					/>
				</Routes>
			</BrowserRouter>
		</ErrorBoundary>
	);
};

const LazyRoutes = React.lazy(() => import(/* webpackChunkName: "lazyRoutes" */ './LazyRoutes'));

import(/* webpackChunkName: "lazyRoutes", webpackPrefetch: true */ './LazyRoutes');

export default App;
